import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertVariant,
  Breadcrumb,
  BreadcrumbItem,
  Divider,
  Skeleton,
  Tab,
  TabTitleText,
} from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import { observer, useLocalStore } from 'mobx-react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-mobx-router5';
import { AlertGroupProvider } from '@ole-ui/ole-ui-components';
import useMobxStores from 'hooks/useMobxStores';
import useTabs from 'hooks/useTabs';
import ErrorState from 'components/Error/ErrorState';
import ExploreSubscriptions from 'components/ExploreSubscriptions';
import SidebarDetails from 'components/Course/OverviewTab/SidebarDetails';
import { LandingLayout } from 'components/Landing/LandingLayout';
import SidebarSkeleton from 'components/Landing/SidebarSkeleton';
import DeleteSkillsPathModal from './DeleteSkillsPathModal';
import ForbiddenView from '../ForbiddenView';
import OfferingsTab from './OfferingsTab';
import OverviewTab from './OverviewTab';
import SidebarButtons from './SidebarButtons';
import { SkillsPathLandingTab } from './types';
import SkillsPathLandingStore from './store';

const SkillsPathLanding = () => {
  const { t, i18n } = useTranslation();
  const { addAlert } = AlertGroupProvider.useAlertGroupContext();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    catalogStore,
    eventStore,
    featuresStore,
    learningPathStore,
    routerStore,
    uiStore,
    userStore,
    vocabularyStore,
  } = useMobxStores();

  const { route } = routerStore;
  const { code } = (route as any).params;

  const {
    fetchSkillsPathData,
    title,
    isError,
    errorStatus,
    isPageLoading,
    isSidebarLoaded,
    showForbidden,
    learningPath,
    aggregatedMetadata,
    showCustomizeButton,
    showDeleteButton,
    language,
    isEnrolling,
    isJoined,
    joinLearningPath,
    unjoinLearningPath,
  } = useLocalStore(
    () =>
      new SkillsPathLandingStore(
        uiStore,
        catalogStore,
        vocabularyStore,
        userStore,
        learningPathStore,
        featuresStore,
      ),
  );

  const handleNavigate = React.useCallback(
    (tab: SkillsPathLandingTab) => {
      const params: Record<string, string> = { code };
      if (tab !== SkillsPathLandingTab.Overview) params.tab = tab;
      routerStore.navigate('skills-paths:view', params);
    },
    [code, routerStore],
  );

  const { activeTabKey, handleTabClick } = useTabs<SkillsPathLandingTab>({
    route: route as { params?: Record<string, string> },
    defaultTab: SkillsPathLandingTab.Overview,
    isValidTab: (v): v is SkillsPathLandingTab =>
      Object.values(SkillsPathLandingTab).includes(v as SkillsPathLandingTab),
    onNavigate: handleNavigate,
  });

  useEffect(() => {
    if (code) {
      fetchSkillsPathData(code);
    }
  }, [code, fetchSkillsPathData]);

  const handleEnroll = useCallback(async () => {
    const pathCode = learningPath?.code ?? code;
    if (!pathCode) return;
    try {
      await joinLearningPath(pathCode);
      addAlert({
        key: `skills-path-enroll-success-${Date.now()}`,
        title: t('Successfully enrolled'),
        variant: AlertVariant.success,
        timeout: 5000,
      });
    } catch {
      addAlert({
        key: `skills-path-enroll-error-${Date.now()}`,
        title: t('Could not enroll'),
        children: t(
          'Something went wrong. Please try again. If the problem persists, contact support.',
        ),
        variant: AlertVariant.danger,
        timeout: 8000,
      });
    }
  }, [learningPath?.code, code, joinLearningPath, addAlert, t]);

  const handleUnenroll = useCallback(async () => {
    const pathCode = learningPath?.code ?? code;
    if (!pathCode) return;
    try {
      await unjoinLearningPath(pathCode);
      addAlert({
        key: `skills-path-unenroll-success-${Date.now()}`,
        title: t('Successfully unenrolled'),
        variant: AlertVariant.success,
        timeout: 5000,
      });
    } catch {
      addAlert({
        key: `skills-path-unenroll-error-${Date.now()}`,
        title: t('Could not unenroll'),
        children: t(
          'Something went wrong. Please try again. If the problem persists, contact support.',
        ),
        variant: AlertVariant.danger,
        timeout: 8000,
      });
    }
  }, [learningPath?.code, code, unjoinLearningPath, addAlert, t]);

  const handleDelete = useCallback(async () => {
    const pathCode = learningPath?.code ?? code;
    if (!pathCode) return;
    try {
      setIsDeleting(true);
      await learningPathStore.handleDeleteCSK(
        i18n,
        vocabularyStore,
        userStore,
        { code: pathCode },
      );
      addAlert({
        key: `skills-path-delete-success-${Date.now()}`,
        title: t('Successfully deleted'),
        variant: AlertVariant.success,
        timeout: 5000,
      });
      routerStore.navigate('skills-paths');
      setIsDeleteModalOpen(false);
    } catch {
      addAlert({
        key: `skills-path-delete-error-${Date.now()}`,
        title: t('Could not delete'),
        children: t(
          'Something went wrong. Please try again. If the problem persists, contact support.',
        ),
        variant: AlertVariant.danger,
        timeout: 8000,
      });
    } finally {
      setIsDeleting(false);
    }
  }, [
    learningPath?.code,
    code,
    learningPathStore,
    i18n,
    vocabularyStore,
    userStore,
    addAlert,
    routerStore,
    t,
  ]);

  if (showForbidden) {
    return (
      <LandingLayout>
        <ForbiddenView />
      </LandingLayout>
    );
  }

  if (isError) {
    const isForbidden = errorStatus === 403;
    return (
      <ErrorState
        showExploreSubscriptions={false}
        onRetry={() => code && fetchSkillsPathData(code)}
        onSupportClick={() => eventStore.toggleDrawer('support_form')}
        title={
          isForbidden
            ? t('You do not have access to this skills path')
            : t('Could not load the skills path')
        }
        body={
          isForbidden
            ? t(
                "This skills path is not included in your current subscription or the owner hasn't made it accessible to you. If you believe this is an error, contact support.",
              )
            : t(
                'Something went wrong. You can try again or contact support if the problem persists.',
              )
        }
      />
    );
  }

  const displayTitle = title || t('Skills Path');

  return (
    <LandingLayout>
      <>
        <Helmet
          title={
            isPageLoading
              ? `${t('Skills Path')} - Red Hat Learning`
              : `${displayTitle} | Overview - Red Hat Learning`
          }
        />
        <LandingLayout.Breadcrumb>
          <Breadcrumb>
            <BreadcrumbItem>
              <Link href routeName="skills-paths">
                {t('Skills Paths')}
              </Link>
            </BreadcrumbItem>
            <BreadcrumbItem isActive>
              {isPageLoading ? (
                <Skeleton width="190px" height="20px" />
              ) : (
                displayTitle
              )}
            </BreadcrumbItem>
          </Breadcrumb>
        </LandingLayout.Breadcrumb>
        {isPageLoading ? (
          <div className="overview__header">
            <Skeleton width="50%" height="28px" />
            <Divider component="div" className="overview__divider" />
          </div>
        ) : (
          <LandingLayout.Header title={displayTitle} />
        )}
        <LandingLayout.Content>
          <LandingLayout.Main>
            <LandingLayout.Tabs
              mountOnEnter={false}
              isBox
              activeKey={activeTabKey}
              onSelect={handleTabClick}
            >
              <Tab
                eventKey={SkillsPathLandingTab.Overview}
                title={<TabTitleText>{t('Overview')}</TabTitleText>}
                aria-label={t(
                  'Skills path overview containing detailed information',
                )}
                data-analytics-id="overview-tab-skills-path-landing-lp"
              >
                <OverviewTab
                  learningPath={learningPath}
                  isLoading={isPageLoading}
                />
              </Tab>

              <Tab
                eventKey={SkillsPathLandingTab.Offerings}
                title={<TabTitleText>{t('Offerings')}</TabTitleText>}
                aria-label={t('View the offerings in this skills path')}
                data-analytics-id="offerings-tab-skills-path-landing-lp"
              >
                <OfferingsTab
                  learningPath={learningPath}
                  isLoading={isPageLoading}
                  lpElementDurations={
                    aggregatedMetadata?.lpElementDurations ?? null
                  }
                />
              </Tab>
            </LandingLayout.Tabs>
          </LandingLayout.Main>

          <LandingLayout.Sidebar>
            {isSidebarLoaded ? (
              <>
                <SidebarButtons
                  pathCode={learningPath?.code ?? code}
                  showCustomizeButton={showCustomizeButton}
                  showDeleteButton={showDeleteButton}
                  isJoined={learningPath?.joined ?? isJoined}
                  isEnrolling={isEnrolling}
                  isReadOnly={learningPath?.read_only === true}
                  onEnroll={handleEnroll}
                  onUnenroll={handleUnenroll}
                  onDeleteClick={() => setIsDeleteModalOpen(true)}
                />

                <ExploreSubscriptions />

                {aggregatedMetadata &&
                  learningPath?.lp_elements?.length > 0 && (
                    <SidebarDetails
                      totalDuration={aggregatedMetadata.totalDuration}
                      totalGuidedExercises={
                        aggregatedMetadata.totalGuidedExercises
                      }
                      totalQuiz={aggregatedMetadata.totalQuiz}
                      totalVideos={aggregatedMetadata.totalVideos}
                      productLabels={aggregatedMetadata.productLabels}
                      audienceLabels={aggregatedMetadata.audienceLabels}
                      topicLabels={aggregatedMetadata.topicLabels}
                      language={language}
                    />
                  )}

                <DeleteSkillsPathModal
                  isOpen={isDeleteModalOpen}
                  onClose={() => setIsDeleteModalOpen(false)}
                  onConfirm={handleDelete}
                  isDeleting={isDeleting}
                />
              </>
            ) : (
              <SidebarSkeleton showCommunity={false} />
            )}
          </LandingLayout.Sidebar>
        </LandingLayout.Content>
      </>
    </LandingLayout>
  );
};

export default observer(SkillsPathLanding);
